/**
 * Custom hook to sync director data to representative fields
 * Handles the director selection checkbox logic and bidirectional sync
 */

import { useEffect } from "react";
import { ShareholderDirector } from "../../../features/merchant/rep";
import { getDirectorEmails, findDirectorByEmail } from "../utils/directorHelpers";

interface UseDirectorSyncProps {
  checked: boolean;
  directorChecked: boolean;
  setDirectorChecked: (value: boolean) => void;
  repEmail: string;
  shareholdersDirectors: ShareholderDirector[];
  updateFields: (updates: {
    repFirstName?: string;
    repLastName?: string;
    repEmail?: string;
    representative_title?: string;
  }) => void;
  clearRepresentativeErrors: () => void;
}

/**
 * Hook to automatically sync director data to representative fields
 * Handles complex logic for director selection and validation
 */
export const useDirectorSync = ({
  checked,
  directorChecked,
  setDirectorChecked,
  repEmail,
  shareholdersDirectors,
  updateFields,
  clearRepresentativeErrors,
}: UseDirectorSyncProps) => {
  useEffect(() => {
    // Don't sync if auth user is acting as representative
    if (checked) return;

    const directorEmails = getDirectorEmails(shareholdersDirectors);
    const allDirectorsEmailPresent = directorEmails.length > 0;

    if (directorChecked) {
      // If selected email is not in directors list, clear fields
      if (!directorEmails.includes(repEmail)) {
        updateFields({
          repFirstName: "",
          repLastName: "",
          representative_title: "",
          repEmail: "",
        });
        return;
      }

      // If email exists in directors, populate fields from director info
      if (directorEmails.includes(repEmail)) {
        const directorInfo = findDirectorByEmail(shareholdersDirectors, repEmail);
        if (directorInfo) {
          updateFields({
            repFirstName: directorInfo.firstName,
            repLastName: directorInfo.lastName,
            representative_title: "Director",
            repEmail: directorInfo.email,
          });
          clearRepresentativeErrors();
        }
      }

      // If no directors have emails, uncheck director selection
      if (!allDirectorsEmailPresent) {
        setDirectorChecked(false);
      }
    }
  }, [
    checked,
    directorChecked,
    repEmail,
    shareholdersDirectors,
    setDirectorChecked,
    updateFields,
    clearRepresentativeErrors,
  ]);
};
